import { useEffect, useState } from "react";
import { useGoogleStorageImage } from "../../../hooks/useGoogleStorageImage";
import { ButtonSecondary, ButtonLight } from "../../../components/ctas";
import { LoaderInline } from "../../../components/loaders";

const GCloudImagesPage = () => {
  const { gcloudListImages, gcloudDeleteImage, gcloudGetImage, environment } = useGoogleStorageImage();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingImage, setDeletingImage] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [imageUrls, setImageUrls] = useState({});
  const [filterByEnvironment, setFilterByEnvironment] = useState(true);

  useEffect(() => {
    loadImages();
  }, [filterByEnvironment]);

  const loadImages = async () => {
    try {
      setLoading(true);
      setError(null);
      const imageList = await gcloudListImages(filterByEnvironment);
      imageList.forEach(img => {
        loadImagePreview(img.name);
      });
      setImages(imageList);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadImagePreview = async (filename) => {
    if (imageUrls[filename]) return;
    try {
      const imageData = await gcloudGetImage(filename, false);
      setImageUrls(prev => ({
        ...prev,
        [filename]: imageData.imageUrl
      }));
    } catch (err) {
      console.error(`Failed to load preview for ${filename}:`, err);
    }
  };

  const handleDeleteClick = (imageName) => {
    setConfirmDelete(imageName);
  };

  const handleCancelDelete = () => {
    setConfirmDelete(null);
  };

  const handleConfirmDelete = async (imageName) => {
    try {
      setDeletingImage(imageName);
      setConfirmDelete(null);
      await gcloudDeleteImage(imageName, filterByEnvironment);
      setImages(prev => prev.filter(img => img.name !== imageName));
      // Clean up the image URL from state
      setImageUrls(prev => {
        const updated = { ...prev };
        delete updated[imageName];
        return updated;
      });
    } catch (err) {
      setError(`Failed to delete image: ${err.message}`);
    } finally {
      setDeletingImage(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEnvironmentBadgeColor = (env) => {
    if (env === 'production') return 'tw-bg-red-100 tw-text-red-800';
    if (env === 'development') return 'tw-bg-green-100 tw-text-green-800';
    return 'tw-bg-gray-100 tw-text-gray-800';
  };

  if (loading) {
    return (
      <div className="tw-min-h-screen tw-bg-gray-100 tw-p-4 md:tw-p-8">
        <div className="tw-max-w-7xl tw-mx-auto">
          <h1 className="tw-text-2xl md:tw-text-3xl tw-font-bold tw-text-secondary tw-mb-6">
            Google Cloud Storage Images
          </h1>
          <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
            <LoaderInline />
            <span className="tw-ml-3 tw-text-gray-600">Loading images...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw-min-h-screen tw-bg-gray-100 tw-p-4 md:tw-p-8">
        <div className="tw-max-w-7xl tw-mx-auto">
          <h1 className="tw-text-2xl md:tw-text-3xl tw-font-bold tw-text-secondary tw-mb-6">
            Google Cloud Storage Images
          </h1>
          <div className="tw-bg-red-100 tw-border tw-border-red-400 tw-text-red-700 tw-px-4 tw-py-3 tw-rounded">
            <p className="tw-font-bold">Error</p>
            <p>{error}</p>
            <ButtonSecondary 
              default 
              className="tw-mt-4" 
              onClick={loadImages}
            >
              Try Again
            </ButtonSecondary>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tw-min-h-screen tw-bg-gray-100 tw-p-4 md:tw-p-8">
      <div className="tw-max-w-7xl tw-mx-auto">
        <div className="tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-items-start md:tw-items-center tw-mb-6 tw-gap-4">
          <div>
            <h1 className="tw-text-2xl md:tw-text-3xl tw-font-bold tw-text-secondary">
              Google Cloud Storage Images
            </h1>
            <p className="tw-text-sm tw-text-gray-500 tw-mt-1">
              Current environment: <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-medium ${getEnvironmentBadgeColor(environment)}`}>{environment}</span>
            </p>
          </div>
          <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-3 tw-items-start sm:tw-items-center">
            <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-600 tw-cursor-pointer">
              <input
                type="checkbox"
                checked={filterByEnvironment}
                onChange={(e) => setFilterByEnvironment(e.target.checked)}
                className="tw-w-4 tw-h-4 tw-rounded tw-border-gray-300"
              />
              Show only {environment} images
            </label>
            <ButtonLight default onClick={loadImages}>
              Refresh
            </ButtonLight>
          </div>
        </div>

        {images.length === 0 ? (
          <div className="tw-bg-white tw-rounded-lg tw-shadow tw-p-8 tw-text-center">
            <p className="tw-text-gray-600">
              {filterByEnvironment 
                ? `No images found for the ${environment} environment.`
                : 'No images found in the bucket.'
              }
            </p>
            {filterByEnvironment && (
              <button 
                onClick={() => setFilterByEnvironment(false)}
                className="tw-mt-2 tw-text-blue-600 tw-underline tw-text-sm"
              >
                Show all environments
              </button>
            )}
          </div>
        ) : (
          <>
            <p className="tw-text-gray-600 tw-mb-4">
              {images.length} image{images.length !== 1 ? 's' : ''} found
              {filterByEnvironment && ` in ${environment} environment`}
            </p>
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-4 md:tw-gap-6">
              {images.map((image) => (
                <div
                  key={image.id || image.name}
                  className="tw-bg-white tw-rounded-lg tw-shadow tw-overflow-hidden tw-flex tw-flex-col"
                  onMouseEnter={() => loadImagePreview(image.name)}
                >
                  {/* Image Preview */}
                  <div className="tw-h-40 tw-bg-gray-200 tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-relative">
                    {imageUrls[image.name] ? (
                      <img
                        src={imageUrls[image.name]}
                        alt={image.name}
                        className="tw-w-full tw-h-full tw-object-cover"
                      />
                    ) : (
                      <div className="tw-text-gray-400 tw-text-sm">
                        Loading...
                      </div>
                    )}
                    {/* Environment Badge */}
                    {image.metadata?.environment && (
                      <span className={`tw-absolute tw-top-2 tw-right-2 tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-medium ${getEnvironmentBadgeColor(image.metadata.environment)}`}>
                        {image.metadata.environment}
                      </span>
                    )}
                  </div>

                  {/* Image Details */}
                  <div className="tw-p-4 tw-flex-1 tw-flex tw-flex-col">
                    <h3 
                      className="tw-font-semibold tw-text-secondary tw-text-sm tw-break-all tw-mb-2"
                      title={image.name}
                    >
                      {image.name}
                    </h3>
                    <div className="tw-text-xs tw-text-gray-500 tw-space-y-1 tw-mb-4">
                      <p>Size: {formatFileSize(image.size)}</p>
                      <p>Type: {image.contentType || 'Unknown'}</p>
                      <p>Created: {formatDate(image.timeCreated)}</p>
                      {image.metadata?.uploadedByName && (
                        <p>Uploaded by: {image.metadata.uploadedByName}</p>
                      )}
                      {image.metadata?.uploadedAt && (
                        <p>Uploaded: {formatDate(image.metadata.uploadedAt)}</p>
                      )}
                    </div>

                    {/* Delete Button / Confirmation */}
                    <div className="tw-mt-auto">
                      {deletingImage === image.name ? (
                        <div className="tw-flex tw-items-center tw-justify-center tw-py-2 tw-text-gray-600">
                          <LoaderInline />
                          <span className="tw-ml-2 tw-text-sm">Deleting...</span>
                        </div>
                      ) : confirmDelete === image.name ? (
                        <div className="tw-space-y-2">
                          <p className="tw-text-xs tw-text-red-600 tw-text-center">
                            Are you sure you want to delete this image?
                          </p>
                          <div className="tw-flex tw-gap-2">
                            <ButtonLight 
                              default 
                              className="tw-flex-1 tw-text-xs"
                              onClick={handleCancelDelete}
                            >
                              Cancel
                            </ButtonLight>
                            <ButtonSecondary 
                              default 
                              className="tw-flex-1 tw-text-xs tw-bg-red-600 hover:tw-bg-red-700"
                              onClick={() => handleConfirmDelete(image.name)}
                            >
                              Delete
                            </ButtonSecondary>
                          </div>
                        </div>
                      ) : (
                        <ButtonSecondary 
                          default 
                          className="tw-w-full tw-text-xs"
                          onClick={() => handleDeleteClick(image.name)}
                        >
                          Delete
                        </ButtonSecondary>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GCloudImagesPage;
